import * as Dialog from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { Button, Input } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import type { ConfirmTone } from "@/lib/confirm"
import { cn } from "@/lib/utils"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  message: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
  requirePassword?: boolean
  passwordLabel?: string
  /** Second action in place of the cancel button; the dialog then closes only by Escape, the × or a click outside. */
  alternativeLabel?: string
  alternativeTone?: ConfirmTone
  onAlternative?: () => void
  onConfirm: (password?: string) => void
  overlayClassName?: string
  contentClassName?: string
}

export function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = "default",
  requirePassword = false,
  passwordLabel,
  alternativeLabel,
  alternativeTone = "default",
  onAlternative,
  onConfirm,
  overlayClassName,
  contentClassName,
}: Props) {
  const { t } = useTranslation("common")
  const confirmRef = useRef<HTMLButtonElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const [password, setPassword] = useState("")
  const resolvedTitle = title ?? t("confirmTitle")
  const resolvedPasswordLabel = passwordLabel ?? t("password")

  useEffect(() => {
    if (!open) {
      setPassword("")
      return
    }
    const id = window.setTimeout(() => {
      if (requirePassword) passwordRef.current?.focus()
      else confirmRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(id)
  }, [open, requirePassword])

  const submit = () => {
    if (requirePassword) {
      const value = password.trim()
      if (!value) {
        passwordRef.current?.focus()
        return
      }
      onConfirm(value)
    } else {
      onConfirm()
    }
    onOpenChange(false)
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "ui-dialog-overlay fixed inset-0 z-[210] bg-[var(--ui-overlay)] backdrop-blur-sm",
            overlayClassName,
          )}
        />
        <Dialog.Content
          className={cn(
            "ui-dialog-content fixed top-1/2 left-1/2 z-[211] w-[min(96vw,400px)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-ui-border bg-ui-bg-elevated p-5 text-ui-text-primary shadow-2xl shadow-black/50 focus:outline-none",
            contentClassName,
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            if (requirePassword) passwordRef.current?.focus()
            else confirmRef.current?.focus()
          }}
        >
          <div className="flex items-start justify-between gap-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ui-text-primary">
              {resolvedTitle}
            </Dialog.Title>
            <Dialog.Close
              type="button"
              className="rounded-lg p-1.5 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
              aria-label={t("close")}
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <Dialog.Description asChild>
            <div className="mt-3 text-sm leading-relaxed whitespace-pre-line text-ui-text-secondary">{message}</div>
          </Dialog.Description>

          {requirePassword ? (
            <label className="mt-4 block">
              <span className="mb-1 block text-xs font-medium text-ui-text-secondary">{resolvedPasswordLabel}</span>
              <Input
                ref={passwordRef}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    submit()
                  }
                }}
              />
            </label>
          ) : null}

          <div className="mt-5 flex flex-wrap justify-end gap-2">
            {alternativeLabel ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={cn(alternativeTone === "danger" && "text-ui-danger hover:text-ui-danger")}
                onClick={() => {
                  onAlternative?.()
                  onOpenChange(false)
                }}
              >
                {alternativeLabel}
              </Button>
            ) : (
              <Button type="button" size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
                {cancelLabel ?? t("cancel")}
              </Button>
            )}
            <Button
              ref={confirmRef}
              type="button"
              size="sm"
              variant={tone === "danger" ? "danger" : "default"}
              disabled={requirePassword && password.trim().length === 0}
              onClick={submit}
            >
              {confirmLabel ?? t("confirm")}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
